import { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import aiStyles from '@/styles/pages/admin/ai.module.less';
import SseStreamUtils from '@/framework/SseStream';
import HttpUtils from '@/framework/HttpUtils';
import Image from '@/components/LocalImage';
import { dispatchIntent } from './intentHandlers';
import './intentRegister';
import { useAGPoolController } from '@/services/contracts';
import { setAGPoolController } from './global-pool';
import { Button, Drawer } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { mergeClasses, uuid } from '@/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MessageHistory from './MessageHistory';
import { AiComponentContext } from '@/context';
import { getMessages } from '@/services/api/ai';

const SmartAssistant = (): ReactElement => {
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            role: 'assistant',
            content: 'I am AIcade Galaxy AI, how can I help you?',
        },
    ]);
    const { openHistory, setOpenHistory, conversationId } = useContext(AiComponentContext);
    const [input, setInput] = useState('');
    const [sessionId, setSessionId] = useState('');
    const agPoolController = useAGPoolController();
    useEffect(() => {
        setAGPoolController(agPoolController);
    }, [agPoolController]);

    const typingMessageRef = useRef<ChatMessage | null>(null);

    // 缓冲区：等待打字的字符
    const typingBufferRef = useRef<any>([]);
    // 定时器
    const typingIntervalRef = useRef<any>(null);

    //事件监听
    const eventSource = useCallback(() => {
        console.log('Initializing AI client...');
        let sessionId = uuid();
        if (conversationId) {
            sessionId = conversationId;
        }
        setSessionId(sessionId);
        const eventSource = SseStreamUtils.createEventSource(`/chat/stream?sessionId=${sessionId}`, {
            withCredentials: true,
        });
        const doneFlagRef = { current: false };
        eventSource.onmessage = (event) => {
            let parsedIntent = null;
            try {
                parsedIntent = JSON.parse(event.data);
            } catch (e) {
                // 普通文本，继续走后续逻辑
            }
            if (parsedIntent && parsedIntent.intent) {
                parsedIntent.address = sessionId; // 确保意图中包含地址
                dispatchIntent(parsedIntent.intent, parsedIntent);
                return;
            }

            if (parsedIntent.text === '[DONE]') {
                doneFlagRef.current = true;
            } else {
                console.log('Received message:', JSON.stringify(event.data));
                // 将新的文本拆成字符，压入缓冲区
                typingBufferRef.current.push(...parsedIntent.text);
                // 如果没有在打字，则开启定时器
                if (!typingIntervalRef.current) {
                    typingIntervalRef.current = setInterval(() => {
                        if (typingBufferRef.current.length > 0) {
                            const nextChar = typingBufferRef.current.shift()!;
                            if (typingMessageRef.current) {
                                typingMessageRef.current.content += nextChar;
                            } else {
                                typingMessageRef.current = {
                                    role: 'assistant',
                                    content: nextChar,
                                };
                                setMessages((prev) => {
                                    prev.push(typingMessageRef.current!);
                                    return [...prev];
                                });
                                return;
                            }
                            // 强制刷新 UI
                            setMessages((prev) => [...prev]);
                        } else {
                            if (doneFlagRef.current) {
                                clearInterval(typingIntervalRef.current);
                                typingIntervalRef.current = null;
                                if (typingMessageRef.current) {
                                    typingMessageRef.current = null;
                                }
                                // setRefresh(refresh + 1); // 强制刷新以更新状态
                                doneFlagRef.current = false;
                            } else {
                                clearInterval(typingIntervalRef.current);
                                typingIntervalRef.current = null;
                            }
                        }
                    }, 50);
                }
            }
        };

        eventSource.onerror = (error) => {
            console.error('Error in SSE connection:', error);
            eventSource.close();
        };

        return () => {
            eventSource.close();
            clearInterval(typingIntervalRef.current);
        };
    }, [conversationId]);

    useEffect(() => {
        eventSource();
    }, [eventSource]);
    //点击获取历史消息
    const loadMsgByConversationId = useCallback(async () => {
        if (conversationId) {
            const data = await getMessages(conversationId);
            setMessages(data);
            setSessionId(conversationId);
        }
    }, [conversationId]);

    useEffect(() => {
        loadMsgByConversationId();
    }, [loadMsgByConversationId]);

    const sendMessage = async () => {
        if (!input.trim()) return;
        const newMessage = { role: 'user', promptId: 'mint', content: input } as ChatMessage;
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setInput('');
        try {
            await HttpUtils.Post(`/chat/message?sessionId=${sessionId}`, { role: 'user', promptId: 'mint', content: input });
        } catch (error) {
            setMessages((prev) => {
                prev.push({ role: 'system', content: 'The current account points are insufficient and need to be exchanged for more AGS points ...' });
                return [...prev];
            });
        }
    };

    const onKeyEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={aiStyles['container']}>
            <div className={aiStyles['chat-box']}>
                {messages.map((msg, i) => (
                    <div
                        key={i}
                        className={aiStyles['messages']}
                        style={{
                            justifyContent: msg.role === 'user' ? 'right' : 'left',
                        }}>
                        <Image className={msg.role === 'user' ? aiStyles['avatar-none'] : aiStyles['avatar']} name="logo" type="svg" dir="/images" />
                        <div className={mergeClasses(aiStyles['message'], msg.role === 'user' ? aiStyles['user-message'] : '')}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: (props) => <a {...props} target="_blank" rel="noreferrer" style={{ color: '#1890ff' }} />,
                                }}>
                                {msg.content}
                            </ReactMarkdown>
                        </div>
                    </div>
                ))}
            </div>

            <div className={aiStyles['input-box']}>
                <textarea value={input} onKeyDown={onKeyEnter} onChange={(e) => setInput(e.target.value)} placeholder="Type a message..." className={aiStyles['input']} />
                <div className={aiStyles['input-box-right']}>
                    <div className={aiStyles['input-box-right-history']} onClick={() => setOpenHistory(true)}>
                        history
                    </div>
                    <Button onClick={() => sendMessage()} className={aiStyles['send-button']} shape="circle" type="primary" icon={<SendOutlined />} size="small" />
                </div>
            </div>

            <Drawer title="Message History" placement="left" closable={false} onClose={() => setOpenHistory(false)} open={openHistory} getContainer={false}>
                <MessageHistory />
            </Drawer>
        </div>
    );
};

export default SmartAssistant;
